/**
 * PI-432: what period a market-indicator tile's number covers, and how fresh
 * it is. A KPI tile shows one number with no axis and no context, so without
 * this the reader cannot tell whether they are looking at today or last year.
 *
 * Pure functions — the component just renders what these return.
 */
import { parseKpiQuery } from '@/features/custom-dashboards/domain/kpiQuery';
import { formatDisplayDate } from '@/utils/helpers';

/** `now`, a relative token (`last_7d`), or a period keyword (`this_month`). */
const RELATIVE_TOKEN = /^(last|next)_\d+[smhdwMy]$/;

function isTokenLike(value) {
  return typeof value === 'string' && /^[a-z][a-z0-9_]*$/.test(value);
}

/**
 * Translate a range token using the SAME vocabulary the date picker uses, so a
 * tile and a chart name the same period identically. Falls back to the raw
 * token rather than inventing wording.
 */
function translateToken(token, t) {
  if (!token) return null;
  if (token === 'now') {
    const now = t?.('dataset.custom_range.now');
    return now && now !== 'dataset.custom_range.now' ? now : 'now';
  }
  const key = `dataset.date_options.${token}`;
  const translated = t?.(key);
  return translated && translated !== key ? translated : token;
}

/** An absolute boundary renders in the standard dd-mm-yyyy notation (PI-436). */
function formatBoundary(value, t) {
  if (value === 'now' || RELATIVE_TOKEN.test(String(value)) || isTokenLike(value)) {
    return translateToken(String(value), t);
  }
  return formatDisplayDate(value, { fallback: null });
}

/**
 * The period a tile's value covers.
 * @param {{controlOverrides?: object, kpiQuery?: string, t?: Function}} options
 * @returns {string|null} label, or null when no period can be determined
 */
export function resolveKpiPeriodLabel({ controlOverrides, kpiQuery, t } = {}) {
  // A dashboard-level control override is what actually gets sent, so it wins.
  const dateRange = controlOverrides?.dateRange || {};
  if (dateRange.period) {
    return translateToken(dateRange.period, t);
  }
  if (dateRange.start && dateRange.end) {
    const start = formatBoundary(dateRange.start, t);
    const end = formatBoundary(dateRange.end, t);
    if (start && end) return `${start} – ${end}`;
  }

  // Otherwise the period is baked into the query's range()/at() segment.
  if (typeof kpiQuery === 'string' && kpiQuery.trim()) {
    const parsed = parseKpiQuery(kpiQuery);
    const token = parsed?.time?.value;
    if (token) return translateToken(token, t);
  }

  return null;
}

/**
 * The freshest timestamp across a tile's series, in standard notation.
 * @param {Array<{last_updated?: string}>} series
 * @returns {string|null}
 */
export function resolveKpiFreshnessLabel(series) {
  const timestamps = (series || [])
    .map((entry) => entry?.last_updated)
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (timestamps.length === 0) return null;
  const newest = new Date(Math.max(...timestamps.map((date) => date.getTime())));
  return formatDisplayDate(newest, { time: true, fallback: null });
}
